import { useEffect, useRef, useState } from "react";
import firebase from "firebase/app";
import "firebase/firestore";

const PUBLIC_STATUSES = ["pending", "accepted", "granted"];

function usePatentApplications() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = firebase
      .firestore()
      .collection("patentApplications")
      .where("displayOnWebsite", "==", true)
      .orderBy("sortOrder")
      .onSnapshot(
        (snap) => {
          setItems(
            snap.docs
              .map((d) => ({ id: d.id, ...d.data() }))
              .filter((p) => PUBLIC_STATUSES.includes(p.status))
          );
          setLoading(false);
        },
        () => setLoading(false)
      );
    return unsubscribe;
  }, []);

  return { items, loading };
}

export default function FooterPatentList({ className }) {
  const { items, loading } = usePatentApplications();
  const ref = useRef(null);
  const hasScrolled = useRef(false);

  // Scroll after Firestore loads and page layout settles
  useEffect(() => {
    if (loading || hasScrolled.current) return;
    if (window.location.hash === "#patents" && ref.current) {
      hasScrolled.current = true;
      requestAnimationFrame(() => {
        if (ref.current) ref.current.scrollIntoView({ behavior: "smooth" });
      });
    }
  }, [loading]);

  const pending = items.filter((p) => p.status !== "granted");
  const granted = items.filter((p) => p.status === "granted");

  const nowrap = { whiteSpace: "nowrap" };
  const list = (rows, num) =>
    rows.map((p, i) => (
      <span key={p.id}>
        {i > 0 && " · "}
        <span style={nowrap}>{`${p.countryName} ${num(p) ?? ""}`.trim()}</span>
      </span>
    ));

  const inherit = { font: "inherit", color: "inherit", letterSpacing: "inherit", margin: 0 };

  return (
    <div id="patents" ref={ref} className={className} style={className ? undefined : inherit} aria-label="Patent and design applications">
      {pending.length > 0 && (
        <p style={inherit}>
          Patent pending:
          <br />
          {list(pending, (p) => p.applicationNumber ?? p.publicationNumber)}
        </p>
      )}
      {granted.length > 0 && (
        <p style={inherit}>
          Patents: {list(granted, (p) => p.patentNumber)}
        </p>
      )}
      <p style={inherit}>
        Australian registered designs{" "}
        <span style={nowrap}>202410040</span> and{" "}
        <span style={nowrap}>202410041</span>
      </p>
    </div>
  );
}
